// @givmo/org-console — a kept ADDRESS on its card, and the door to record and correct it (Org
// Machinery OM-64 and OM-59, Design 4C §3 and §8; the frames approved by D-CPO-69).
//
// The approved seam-A card shape — title, description, then the value, a refusal under it, the
// last-change line and the `sr-only` status — with an address where a knob has its control:
//
//   * NOT SET: "Not set", with "Add address" — and, where the organization's outside record holds a
//     mailing address, the shortcut that copies it ("Same as mailing address", D-CPO-61; M15a). The
//     shortcut is ONE act: it writes the record's address as an ordinary kept value, attributed to
//     whoever used it, and never follows a later change of the record.
//   * NOT SET, AND THE MAILING ADDRESS NAMES A BOX (a P.O. box or a private mailbox, the server's own
//     patterns): there is nothing to copy, so neither the shortcut nor "Add address" is offered — the
//     fields are open at once, under one line saying why, with no Cancel, since there is nothing to go
//     back to (M16). A shortcut the server would refuse contradicted the description above it.
//   * SET: the address on its own lines, the county last with its label, and "Edit". While it is the
//     record's mailing address (line 1, line 2, city, state and ZIP, any case) it reads "Same as your
//     mailing address" instead of repeating it (M15b).
//   * EDITING: a form inside the card — Street address; Apartment, suite (optional); City; State (the
//     served states, under the served placeholder); ZIP code; County (optional) — then Save and
//     Cancel (M03). Seam B's law for a multi-field form: the save is explicit and dirty-gated, and the
//     form registers its dirty state with the page's unsaved-changes guard.
//
// Every refusal is the server's line, never this card's copy. An address refused part by part (422
// `INVALID_SETTING_VALUE`, `context.field`) puts the line under its field and keeps what was typed
// (M05). A stale view (409 `SETTING_STALE`) closes the form: the card then shows the address as the
// record now holds it, the stale line, and who changed it (M06) — the line said only once the re-read
// has answered, and only while the record does not hold what was asked (compared part by part, never
// by identity). There is no "remove the address" control at birth; a reset stays possible through
// the API.
//
// DATA CONTRACT: the composer's `<prefix>-def-<key>` (the card) · `-value` · `-add` · `-same-as` ·
// `-edit` · `-form` · `-box-reason` · `-field-<part>` · `-save` · `-cancel` · `-error` ·
// `-last-change` · `-status`.

import { useEffect, useId, useState, type FormEvent } from 'react';
import { firstErrorCode, firstErrorContext, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Field,
  Select,
  testIdProp,
} from '@givmo/design-system';
import {
  addressDraftDiffers,
  addressDraftFrom,
  addressHolds,
  addressLines,
  addressMatchesRecord,
  addressValueFrom,
  addressValueFromRecord,
  countyLine,
  labelMidSentence,
  recordAddressBox,
  type OrgAddressDraft,
} from './org-address';
import { decodeOrgAddress } from './org-decode';
import { isOrgSettingStale } from './org-console-transport';
import {
  CardHintLine,
  DefinitionHeader,
  FactValue,
  SETTING_SAVE_FAILED_LINE,
  lastChangeFor,
} from './settings-card-parts';
import { useUpdateOrgSettings } from './use-org-console';
import type {
  OrgAddress,
  OrgRecordAddress,
  OrgSettingDefinition,
  OrgSettingState,
} from './wire-types';

/** The door's words (Design 4C §8; D-CPO-69 question 6). */
export const ADDRESS_ADD_LABEL = 'Add address';
export const ADDRESS_EDIT_LABEL = 'Edit';
export const ADDRESS_SAVE_LABEL = 'Save';
export const ADDRESS_CANCEL_LABEL = 'Cancel';

/** The form's fields, in Design 4C §8's order, "County (optional)" last (D-CPO-69 question 6). */
export const ADDRESS_FIELDS: readonly {
  part: keyof OrgAddressDraft;
  label: string;
  span: string;
  maxLength?: number;
  autoComplete?: string;
}[] = [
  {
    part: 'line1',
    label: 'Street address',
    span: 'col-span-12',
    maxLength: 100,
    autoComplete: 'address-line1',
  },
  {
    part: 'line2',
    label: 'Apartment, suite (optional)',
    span: 'col-span-12',
    maxLength: 100,
    autoComplete: 'address-line2',
  },
  {
    part: 'city',
    label: 'City',
    span: 'col-span-5',
    maxLength: 60,
    autoComplete: 'address-level2',
  },
  { part: 'state', label: 'State', span: 'col-span-4', autoComplete: 'address-level1' },
  {
    part: 'postal_code',
    label: 'ZIP code',
    span: 'col-span-3',
    // "NNNNN-NNNN": a ZIP+4 is admitted, so the field takes a dash (no digits-only keypad).
    maxLength: 10,
    autoComplete: 'postal-code',
  },
  { part: 'county', label: 'County (optional)', span: 'col-span-6', maxLength: 60 },
];

/** The shortcut's word, from the record fact's served label (D-CPO-61): "Same as mailing address". */
export function sameAsLabel(recordLabel: string): string {
  return `Same as ${labelMidSentence(recordLabel)}`;
}

/** What a kept address that IS the record's mailing address reads as: "Same as your mailing address". */
export function sameAsLine(recordLabel: string): string {
  return `Same as your ${labelMidSentence(recordLabel)}`;
}

/**
 * Why the fields are open at once (D-CPO-69 question 6): the mailing address names a box, which is
 * never a physical address. Composed with the record fact's served label ("mailing address").
 */
export function addressBoxReason(kind: 'po_box' | 'private_mailbox', mailingLabel: string): string {
  const what = kind === 'po_box' ? 'a P.O. box' : 'a mail service';
  return `Your ${labelMidSentence(mailingLabel)} is ${what}, so enter the street address where the organization is.`;
}

/** An outside record's address the card may copy, and the record fact's served label. */
export interface OrgAddressSameAs {
  label: string;
  address: OrgRecordAddress;
}

/**
 * The lines a kept address reads as on its card, or `null` when nobody has entered one: the record's
 * mailing address reads "Same as your mailing address" (its county, when kept, still on its line).
 */
export function addressValueLines(
  address: OrgAddress | null,
  sameAs: OrgAddressSameAs | null = null,
): string[] | null {
  if (address === null) return null;
  if (sameAs !== null && addressMatchesRecord(address, sameAs.address)) {
    return [
      sameAsLine(sameAs.label),
      ...(address.county === null ? [] : [countyLine(address.county)]),
    ];
  }
  return addressLines(address);
}

/** A refusal of one part (or of the whole value), as the server served it: the part and its line. */
function partRefusal(error: Error | null): { field: string | null; line: string } | null {
  if (error === null || !isGivmoError(error)) return null;
  if (error.status !== 422 || firstErrorCode(error) !== 'INVALID_SETTING_VALUE') return null;
  const field = firstErrorContext(error)?.field;
  return { field: typeof field === 'string' ? field : null, line: error.message };
}

export interface OrgAddressFactCardProps {
  orgRef: string;
  definition: OrgSettingDefinition;
  /** The organization's STORED value for this key, or `undefined` when it is not set. */
  stored: unknown;
  state: OrgSettingState | undefined;
  /** Whether the page's settings read is re-reading — a refusal's line waits for its answer. */
  refreshing: boolean;
  personName?: (userId: string) => string | undefined;
  /** The record's mailing address the shortcut copies, or `null` where there is none. */
  sameAs?: OrgAddressSameAs | null;
  /** The page's unsaved-changes guard, for the open form. */
  registerDirty?: (dirty: boolean) => void;
  testId: string;
}

export function OrgAddressFactCard({
  orgRef,
  definition,
  stored,
  state,
  refreshing,
  personName,
  sameAs = null,
  registerDirty,
  testId,
}: OrgAddressFactCardProps) {
  const update = useUpdateOrgSettings(orgRef);
  const headingId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<OrgAddressDraft>(() => addressDraftFrom(null));
  const [saved, setSaved] = useState(false);
  const value = decodeOrgAddress(stored);
  const asked = update.variables?.values[definition.key];

  // A mailing address that names a box can never be copied: with nothing kept yet, the fields are open.
  const box = sameAs === null ? null : recordAddressBox(sameAs.address);
  const openAtOnce = value === null && box !== null;
  const formOpen = editing || openAtOnce;
  const dirty = formOpen && addressDraftDiffers(draft, value);
  useEffect(() => {
    registerDirty?.(dirty);
  }, [dirty, registerDirty]);
  useEffect(() => () => registerDirty?.(false), [registerDirty]);

  const shortcut = sameAs === null || box !== null ? null : addressValueFromRecord(sameAs.address);
  const write = (next: Record<string, string | null>, onSaved: () => void) => {
    setSaved(false);
    update.mutate(
      {
        values: { [definition.key]: next },
        expected_revisions: { [definition.key]: state?.revision ?? 0 },
      },
      {
        onSuccess: () => {
          setSaved(true);
          onSaved();
        },
        // A stale view closes the form: the card shows the record as it now stands, and the line.
        onError: (error) => {
          if (isOrgSettingStale(error)) setEditing(false);
        },
      },
    );
  };
  const open = () => {
    update.reset();
    setDraft(addressDraftFrom(value));
    setEditing(true);
  };
  const cancel = () => {
    update.reset();
    setEditing(false);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!dirty || update.isPending) return;
    write(addressValueFrom(draft), () => setEditing(false));
  };
  const change = (part: keyof OrgAddressDraft, next: string) => {
    // A refusal answers what was asked; once the person changes it, it no longer does.
    if (update.isError) update.reset();
    setDraft((current) => ({ ...current, [part]: next }));
  };

  const refusal = partRefusal(update.error);
  const formFields = ADDRESS_FIELDS.map((field) => field.part as string);
  const fieldRefusal =
    formOpen && refusal !== null && refusal.field !== null && formFields.includes(refusal.field)
      ? refusal
      : null;
  // In the form: a refusal of the whole value, or a failure that is neither a part nor a stale view.
  const formLine =
    formOpen && update.isError && fieldRefusal === null && !isOrgSettingStale(update.error)
      ? (refusal?.line ?? SETTING_SAVE_FAILED_LINE)
      : null;
  // On the card: said once the re-read has answered, and only while the record does not hold the ask.
  const cardLine =
    !formOpen && update.isError && !refreshing && !addressHolds(value, asked)
      ? isOrgSettingStale(update.error) && update.error !== null
        ? update.error.message
        : (refusal?.line ?? SETTING_SAVE_FAILED_LINE)
      : null;
  const lastChange = lastChangeFor(state, personName);
  const lines = addressValueLines(value, sameAs);

  return (
    <Card {...testIdProp(testId)}>
      <DefinitionHeader definition={definition} headingId={headingId} />
      <CardBody className="flex flex-col gap-8">
        {formOpen ? (
          <form
            noValidate
            aria-labelledby={headingId}
            onSubmit={submit}
            className="flex max-w-[640px] flex-col gap-16"
            {...testIdProp(`${testId}-form`)}
          >
            {openAtOnce && box !== null && sameAs !== null && (
              // The form's lede, in the card's secondary text: nothing has been refused.
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                {...testIdProp(`${testId}-box-reason`)}
              >
                {addressBoxReason(box, sameAs.label)}
              </p>
            )}
            <div className="grid grid-cols-12 gap-x-12 gap-y-16">
              {ADDRESS_FIELDS.map((field) => {
                const refused = fieldRefusal?.field === field.part ? fieldRefusal.line : undefined;
                if (field.part === 'state') {
                  return (
                    <Select
                      key={field.part}
                      appearance="saas"
                      label={field.label}
                      options={definition.choices.map((choice) => ({
                        value: choice.value,
                        label: choice.label,
                      }))}
                      value={draft.state.length > 0 ? draft.state : null}
                      // The served unset word ("Choose a state"); the web keeps no copy of it.
                      placeholder={definition.placeholder ?? undefined}
                      disabled={update.isPending}
                      onValueChange={(next) => change('state', next ?? '')}
                      error={refused !== undefined}
                      errorMessage={refused}
                      className={field.span}
                      testId={`${testId}-field-${field.part}`}
                    />
                  );
                }
                return (
                  <Field
                    key={field.part}
                    label={field.label}
                    value={draft[field.part]}
                    maxLength={field.maxLength}
                    autoComplete={field.autoComplete ?? 'off'}
                    disabled={update.isPending}
                    onChange={(event) => change(field.part, event.target.value)}
                    error={refused !== undefined}
                    errorMessage={refused}
                    containerClassName={field.span}
                    testId={`${testId}-field-${field.part}`}
                  />
                );
              })}
            </div>
            {formLine !== null && (
              <ErrorMessage testId={`${testId}-error`}>{formLine}</ErrorMessage>
            )}
            <div className="mt-8 flex items-center gap-8">
              <Button
                appearance="saas"
                tone="primary"
                size="md"
                type="submit"
                disabled={!dirty || update.isPending}
                loading={update.isPending}
                testId={`${testId}-save`}
              >
                {ADDRESS_SAVE_LABEL}
              </Button>
              {/* Open at once, there is nothing to go back to: no Cancel. */}
              {editing && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="md"
                  type="button"
                  disabled={update.isPending}
                  onClick={cancel}
                  testId={`${testId}-cancel`}
                >
                  {ADDRESS_CANCEL_LABEL}
                </Button>
              )}
            </div>
          </form>
        ) : (
          <>
            {/* One line (not set, or the record's address) centres on its buttons; an address on
                several lines keeps its Edit beside the first. */}
            <div
              className={`flex justify-between gap-16 ${
                lines === null || lines.length === 1 ? 'items-center' : 'items-start'
              }`}
            >
              <FactValue lines={lines} testId={`${testId}-value`} />
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-8">
                {value === null ? (
                  <>
                    {shortcut !== null && sameAs !== null && (
                      <Button
                        appearance="saas"
                        tone="secondary"
                        size="md"
                        disabled={update.isPending}
                        loading={update.isPending}
                        onClick={() => write(shortcut, () => {})}
                        testId={`${testId}-same-as`}
                      >
                        {sameAsLabel(sameAs.label)}
                      </Button>
                    )}
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="md"
                      disabled={update.isPending}
                      onClick={open}
                      testId={`${testId}-add`}
                    >
                      {ADDRESS_ADD_LABEL}
                    </Button>
                  </>
                ) : (
                  <Button
                    appearance="saas"
                    tone="secondary"
                    size="md"
                    disabled={update.isPending}
                    onClick={open}
                    testId={`${testId}-edit`}
                  >
                    {ADDRESS_EDIT_LABEL}
                  </Button>
                )}
              </div>
            </div>
            {/* A refusal answers the act, so it sits under the value; who last changed it follows —
                after a stale refusal, it says who moved it. */}
            {cardLine !== null && (
              <ErrorMessage testId={`${testId}-error`}>{cardLine}</ErrorMessage>
            )}
            {lastChange !== null && (
              <CardHintLine testId={`${testId}-last-change`}>{lastChange}</CardHintLine>
            )}
          </>
        )}
        <p role="status" aria-live="polite" className="sr-only" {...testIdProp(`${testId}-status`)}>
          {update.isPending ? 'Saving…' : saved ? 'Saved.' : ''}
        </p>
      </CardBody>
    </Card>
  );
}
